import { useMemo, useState } from 'react';
import { Card, Stack, Title, Text, Badge, Group, Button } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { templates as templatesNs } from '@repletoryreach/sdk-api';
import { parseComponents, extractVariables } from './parseComponents';
import { useT } from '../../../../i18n/I18nProvider';

type Template = NonNullable<Awaited<ReturnType<typeof templatesNs.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted)';

export function VariablesTab({ template }: { template: Template }) {
  const t = useT();
  const components = useMemo(() => parseComponents(template.components), [template.components]);
  const vars = useMemo(() => extractVariables(components), [components]);
  const [samples, setSamples] = useState<Record<number, string>>({});

  const body = components.find((c) => c.type === 'BODY')?.text ?? '';
  const filled = useMemo(
    () => body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => samples[Number(n)] ?? `{{${n}}}`),
    [body, samples]
  );

  // The toast used to fire before the clipboard promise settled, so a denied
  // clipboard reported a copy that never happened.
  async function copyFilled() {
    try {
      await navigator.clipboard.writeText(filled);
      notifySuccess(t('templates.view.variables.toast.copied'), t('templates.view.variables.toast.copiedTitle'));
    } catch {
      notifyError(t('templates.view.variables.toast.copyFailed'));
    }
  }

  if (vars.length === 0) {
    return (
      <Alert color="blue" variant="light">
        {t('templates.view.variables.emptyLead')} <code>{'{{N}}'}</code> {t('templates.view.variables.emptyTrail')}
      </Alert>
    );
  }

  const hasSamples = Object.values(samples).some((v) => v.trim().length > 0);

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline" wrap="wrap">
            <Title order={4}>{t('templates.view.variables.detected', { count: vars.length })}</Title>
            <Group gap="sm">
              {vars.map((n) => (
                <Badge key={n} size="xs" variant="light" color="orange">{`{{${n}}}`}</Badge>
              ))}
            </Group>
          </Group>
          <Text role="bodySmall" c={MUTED}>
            {t('templates.view.variables.hint')}
          </Text>
          <Stack gap="sm">
            {vars.map((n) => (
              <TextInput
                key={n}
                label={t('templates.view.variables.form.label', { token: `{{${n}}}` })}
                placeholder={t('templates.view.variables.form.placeholder', { token: `{{${n}}}` })}
                value={samples[n] ?? ''}
                onChange={(e) =>
                  setSamples((s) => ({ ...s, [n]: (e.target as HTMLInputElement).value }))
                }
              />
            ))}
          </Stack>
          {hasSamples && (
            <Group gap="sm">
              <Button size="xs" variant="subtle" onClick={() => setSamples({})}>
                {t('templates.view.variables.reset')}
              </Button>
            </Group>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="baseline">
            <Title order={4}>{t('templates.view.variables.filledPreview')}</Title>
            <Button size="xs" variant="outline" onClick={() => void copyFilled()}>{t('action.copy')}</Button>
          </Group>
          <div
            style={{
              background: 'var(--rr-color-surface-elevated)',
              border: '1px solid var(--rr-color-border)',
              borderRadius: 8,
              padding: 16,
              whiteSpace: 'pre-wrap',
              fontSize: 14
            }}
          >
            {filled || <span style={{ color: MUTED }}>{t('templates.view.variables.noBody')}</span>}
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}
